import { MARKET_COUNTRY_OPTIONS } from "../onboarding/experience-catalog.mjs";

const DEFAULT_EDITION_PATH = "/api/brief/narration/default-edition";
const USER_BUDGET_CODE = "PAID_API_USER_DAILY_BUDGET_EXCEEDED";
const SYSTEM_BUDGET_CODE = "PAID_API_SYSTEM_DAILY_BUDGET_EXCEEDED";
const DEVICE_SPEECH_CODE = "daily_default_audio_device_speech_required";
const LAUNCH_LANGUAGES_BY_COUNTRY = new Map(
  MARKET_COUNTRY_OPTIONS.map((market) => [market.code, new Set(market.languages)]),
);
const LAUNCH_LANGUAGES = new Set(MARKET_COUNTRY_OPTIONS.flatMap((market) => market.languages));

function record(value) {
  return value && typeof value === "object" && !Array.isArray(value) ? value : null;
}

function cleanString(value) {
  return typeof value === "string" ? value.trim() : "";
}

export function normalizeDailyDefaultLanguage(value) {
  const tag = cleanString(value).replaceAll("_", "-").toLowerCase();
  if (!tag) return "";
  if (/^(?:pnb|lah)(?:-|$)/.test(tag) || /^pa-(?:arab(?:-|$)|pk(?:-|$))/.test(tag)) {
    return "pnb";
  }
  const base = tag.split("-")[0];
  return LAUNCH_LANGUAGES.has(base) ? base : "";
}

export function normalizeDailyDefaultCountry(value) {
  const country = cleanString(value).toUpperCase();
  return LAUNCH_LANGUAGES_BY_COUNTRY.has(country) ? country : "";
}

function dailyDefaultMarketLanguageSupported(countryCode, languageCode) {
  const country = normalizeDailyDefaultCountry(countryCode);
  const language = normalizeDailyDefaultLanguage(languageCode);
  return Boolean(country && language && LAUNCH_LANGUAGES_BY_COUNTRY.get(country)?.has(language));
}

export function buildDailyDefaultEditionUrl({ countryCode, languageCode, clientDeviceId }) {
  const country = normalizeDailyDefaultCountry(countryCode);
  const language = normalizeDailyDefaultLanguage(languageCode);
  const deviceId = cleanString(clientDeviceId).slice(0, 128);
  if (!dailyDefaultMarketLanguageSupported(country, language) || !deviceId) {
    throw new TypeError("daily_default_audio_identity_invalid");
  }
  const query = new URLSearchParams({
    countryCode: country,
    languageCode: language,
    voiceId: "auto",
    clientDeviceId: deviceId,
  });
  return `${DEFAULT_EDITION_PATH}?${query.toString()}`;
}

export function dailyDefaultTierFromErrorBody(value) {
  const body = record(value);
  const tier = cleanString(body?.tier);
  return tier === "free" || tier === "trial" || tier === "premium" ? tier : null;
}

function canonicalBudgetEnvelope(error, expectedCode, expectedScope) {
  const body = record(error?.body);
  const bodyCode = cleanString(body?.code);
  const bodyError = cleanString(body?.error);
  const hasCanonicalCode =
    Boolean(bodyCode || bodyError) &&
    (!bodyCode || bodyCode === expectedCode) &&
    (!bodyError || bodyError === expectedCode);
  if (
    error?.status !== 429 ||
    error?.code !== expectedCode ||
    body?.ok !== false ||
    body?.scope !== expectedScope ||
    !hasCanonicalCode
  ) {
    return null;
  }
  return body;
}

export function dailyDefaultOfferForUserBudgetError({ error, countryCode, languageCode }) {
  const body = canonicalBudgetEnvelope(error, USER_BUDGET_CODE, "user");
  if (!body || !dailyDefaultMarketLanguageSupported(countryCode, languageCode)) {
    return null;
  }
  const tier = dailyDefaultTierFromErrorBody(body);
  const showSubscribeCta = body?.showSubscribeCta;
  if (!tier || typeof showSubscribeCta !== "boolean") return null;
  return Object.freeze({
    countryCode: normalizeDailyDefaultCountry(countryCode),
    languageCode: normalizeDailyDefaultLanguage(languageCode),
    tier,
    showSubscribeCta,
  });
}

export function isDailyDefaultSystemBudgetError(error) {
  const body = canonicalBudgetEnvelope(error, SYSTEM_BUDGET_CODE, "system");
  return Boolean(body && dailyDefaultTierFromErrorBody(body) && body.showSubscribeCta === false);
}

export function dailyDefaultOfferForBriefRunError({ error, requestBody }) {
  const request = record(requestBody);
  return dailyDefaultOfferForUserBudgetError({
    error,
    countryCode: request?.country || request?.countryCode,
    languageCode: request?.lang || request?.languageCode,
  });
}

export function dailyDefaultOfferForDashboardError({ path, error, requestBody }) {
  if (path !== "/api/dashboard-briefing-audio") return null;
  const request = record(requestBody);
  return dailyDefaultOfferForUserBudgetError({
    error,
    countryCode: request?.country || request?.countryCode,
    languageCode: request?.lang || request?.languageCode,
  });
}

const MAX_DAILY_DEFAULT_AUDIO_ITEM_BYTES = 12 * 1024 * 1024;
const MAX_DAILY_DEFAULT_AUDIO_PLAYLIST_BYTES = MAX_DAILY_DEFAULT_AUDIO_ITEM_BYTES;
const MAX_DAILY_DEFAULT_AUDIO_ITEM_BASE64_CHARS =
  Math.ceil(MAX_DAILY_DEFAULT_AUDIO_ITEM_BYTES / 3) * 4;

function base64Audio(value) {
  const audioContent = cleanString(value);
  if (
    !audioContent ||
    audioContent.length > MAX_DAILY_DEFAULT_AUDIO_ITEM_BASE64_CHARS ||
    audioContent.length % 4 !== 0 ||
    !/^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/.test(audioContent)
  ) {
    throw new TypeError("daily_default_audio_playlist_invalid");
  }
  return audioContent;
}

function decodedBase64ByteLength(audioContent) {
  const padding = audioContent.endsWith("==") ? 2 : audioContent.endsWith("=") ? 1 : 0;
  return (audioContent.length / 4) * 3 - padding;
}

function validatedDailyDefaultEditionAudio(audioContent) {
  const encoded = base64Audio(audioContent);
  const decodedLength = decodedBase64ByteLength(encoded);
  if (decodedLength <= 0 || decodedLength > MAX_DAILY_DEFAULT_AUDIO_ITEM_BYTES) {
    throw new TypeError("daily_default_audio_playlist_invalid");
  }
  return { encoded, decodedLength };
}

function decodeDailyDefaultEditionAudio({ encoded }) {
  const binary = atob(encoded);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes;
}

function dailyDefaultEditionItemBytes(audioContent) {
  return decodeDailyDefaultEditionAudio(validatedDailyDefaultEditionAudio(audioContent));
}

function dailyDefaultItemTypeForSection(sectionId) {
  if (sectionId === "news" || sectionId.startsWith("news_")) return "NEWS_HIGHLIGHTS";
  if (sectionId === "market_pulse") return "MARKET_PULSE";
  if (sectionId === "cricket" || sectionId.startsWith("sport_")) {
    return "SPORTS_COUNTRY_PRIMARY";
  }
  if (sectionId === "quote") return "QUOTE_OF_THE_DAY";
  return "";
}

function attestDailyDefaultEditionItems(items, defaultSectionIds) {
  const expected = (Array.isArray(defaultSectionIds) ? defaultSectionIds : [])
    .map(cleanString)
    .filter((sectionId) => sectionId !== "watchlist" && sectionId !== "world_affairs")
    .map((sectionId) => ({
      sectionId,
      itemType: dailyDefaultItemTypeForSection(sectionId),
      optional: sectionId === "cricket" || sectionId.startsWith("sport_"),
    }));
  if (
    expected.length === 0 ||
    new Set(expected.map((row) => row.sectionId)).size !== expected.length ||
    expected.some((row) => !row.itemType)
  ) {
    throw new TypeError("daily_default_audio_playlist_invalid");
  }
  let itemIndex = 0;
  for (const expectedItem of expected) {
    const item = items[itemIndex];
    if (item?.sectionId === expectedItem.sectionId) {
      if (item.itemType !== expectedItem.itemType) {
        throw new TypeError("daily_default_audio_playlist_invalid");
      }
      itemIndex += 1;
    } else if (!expectedItem.optional) {
      throw new TypeError("daily_default_audio_playlist_invalid");
    }
  }
  if (itemIndex !== items.length) {
    throw new TypeError("daily_default_audio_playlist_invalid");
  }
}

export function normalizeDailyDefaultEdition(value, expected) {
  const edition = record(value);
  const countryCode = normalizeDailyDefaultCountry(expected?.countryCode);
  const languageCode = normalizeDailyDefaultLanguage(expected?.languageCode);
  if (
    edition?.ok !== true ||
    edition?.format !== "audio-playlist" ||
    !dailyDefaultMarketLanguageSupported(countryCode, languageCode) ||
    normalizeDailyDefaultCountry(edition.countryCode) !== countryCode ||
    normalizeDailyDefaultLanguage(edition.languageCode) !== languageCode ||
    edition.voiceId !== "auto" ||
    !/^\d{4}-\d{2}-\d{2}$/.test(cleanString(edition.dateKey)) ||
    !Array.isArray(edition.items) ||
    edition.items.length < 1 ||
    edition.items.length > 24
  ) {
    throw new TypeError("daily_default_audio_playlist_invalid");
  }
  const seen = new Set();
  const items = edition.items.map((rawItem) => {
    const item = record(rawItem);
    const sectionId = cleanString(item?.sectionId);
    const itemType = cleanString(item?.itemType);
    if (
      !/^[a-z0-9][a-z0-9_-]{0,95}$/i.test(sectionId) ||
      !/^[A-Z][A-Z0-9_]{0,63}$/.test(itemType) ||
      seen.has(sectionId)
    ) {
      throw new TypeError("daily_default_audio_playlist_invalid");
    }
    seen.add(sectionId);
    return Object.freeze({
      sectionId,
      itemType,
      audioContent: base64Audio(item?.audioContent),
    });
  });
  attestDailyDefaultEditionItems(items, expected?.defaultSectionIds);
  return Object.freeze({
    countryCode,
    languageCode,
    dateKey: edition.dateKey,
    voiceId: "auto",
    items: Object.freeze(items),
  });
}

export function dailyDefaultDeviceSpeechResponseMatches(value, expected) {
  const body = record(value);
  return Boolean(
    body?.ok === false &&
    body?.error === DEVICE_SPEECH_CODE &&
    body?.format === "device-speech" &&
    dailyDefaultMarketLanguageSupported(expected?.countryCode, expected?.languageCode) &&
    normalizeDailyDefaultCountry(body.countryCode) ===
      normalizeDailyDefaultCountry(expected?.countryCode) &&
    normalizeDailyDefaultLanguage(body.languageCode) ===
      normalizeDailyDefaultLanguage(expected?.languageCode) &&
    body.voiceId === "auto" &&
    /^\d{4}-\d{2}-\d{2}$/.test(cleanString(body.dateKey)),
  );
}

export function dailyDefaultUnavailableResponseMatches(value, expected) {
  const body = record(value);
  return Boolean(
    body?.ok === false &&
    body?.error === "daily_default_audio_unavailable" &&
    dailyDefaultMarketLanguageSupported(expected?.countryCode, expected?.languageCode) &&
    normalizeDailyDefaultCountry(body.countryCode) ===
      normalizeDailyDefaultCountry(expected?.countryCode) &&
    normalizeDailyDefaultLanguage(body.languageCode) ===
      normalizeDailyDefaultLanguage(expected?.languageCode) &&
    body.voiceId === "auto" &&
    /^\d{4}-\d{2}-\d{2}$/.test(cleanString(body.dateKey)),
  );
}

function boundedSpeechFact(value, maxChars) {
  return cleanString(value).replace(/\s+/g, " ").slice(0, maxChars).trim();
}

function distinctSpeechFacts(values) {
  const seen = new Set();
  const facts = [];
  for (const value of values) {
    const fact = boundedSpeechFact(value, 1_200);
    const key = fact.toLocaleLowerCase();
    if (!fact || seen.has(key)) continue;
    seen.add(key);
    facts.push(fact);
  }
  return facts;
}

function articleSpeechBody(section) {
  const articles = Array.isArray(section?.articles) ? section.articles.slice(0, 6) : [];
  return articles
    .map((rawArticle) => {
      const article = record(rawArticle);
      if (!article) return "";
      return distinctSpeechFacts([
        boundedSpeechFact(article.title, 400),
        boundedSpeechFact(article.description, 1_200),
      ]).join(". ");
    })
    .filter(Boolean)
    .join(". ");
}

function sportsSpeechBody(section) {
  const matches = Array.isArray(section?.narrationMatches)
    ? section.narrationMatches.slice(0, 3)
    : [];
  return matches
    .map((rawMatch) => {
      const match = record(rawMatch);
      if (!match) return "";
      const homeTeam = boundedSpeechFact(match.homeTeam, 240);
      const awayTeam = boundedSpeechFact(match.awayTeam, 240);
      const matchup =
        homeTeam && awayTeam
          ? `${homeTeam} — ${awayTeam}`
          : boundedSpeechFact(match.teams || match.matchup, 480);
      return distinctSpeechFacts([
        matchup,
        match.competitionName,
        match.competitionRound,
        match.matchType,
        match.scoreLine,
        match.resultLine,
        match.detailLine,
        match.kickoffLabel,
        match.venue,
      ]).join(". ");
    })
    .filter(Boolean)
    .join(". ");
}

function canonicalSectionForLocalSpeech(section) {
  const body = cleanString(section?.body);
  if (body) return section;
  const id = cleanString(section?.id);
  const structuredBody =
    id === "news" || id.startsWith("news_")
      ? articleSpeechBody(section)
      : id === "cricket" || id.startsWith("sport_")
        ? sportsSpeechBody(section)
        : "";
  return structuredBody ? { ...section, body: structuredBody } : null;
}

export function canonicalDailyDefaultSpeechBody({
  requestBody,
  defaultSectionIds,
  countryCode,
  languageCode,
}) {
  const request = record(requestBody);
  const country = normalizeDailyDefaultCountry(countryCode);
  const language = normalizeDailyDefaultLanguage(languageCode);
  if (!dailyDefaultMarketLanguageSupported(country, language)) return null;
  const sections = Array.isArray(record(request?.facts)?.briefSections)
    ? request.facts.briefSections
    : [];
  const canonicalIds = (Array.isArray(defaultSectionIds) ? defaultSectionIds : [])
    .map(cleanString)
    .filter((id) => id && id !== "watchlist" && id !== "world_affairs");
  if (!canonicalIds.length || new Set(canonicalIds).size !== canonicalIds.length) return null;
  const canonicalIdSet = new Set(canonicalIds);
  const byId = new Map();
  const seenCanonicalIds = new Set();
  for (const rawSection of sections) {
    const section = record(rawSection);
    const id = cleanString(section?.id);
    if (!canonicalIdSet.has(id)) continue;
    if (seenCanonicalIds.has(id)) return null;
    seenCanonicalIds.add(id);
    const localSpeechSection = canonicalSectionForLocalSpeech(section);
    if (localSpeechSection) byId.set(id, localSpeechSection);
  }
  const canonicalSections = canonicalIds.map((id) => byId.get(id)).filter(Boolean);
  if (!canonicalSections.length) return null;
  return {
    ...request,
    country,
    lang: language,
    smartSummaryText: "",
    facts: { briefSections: canonicalSections },
  };
}

export function dailyDefaultEditionItemBlob(audioContent) {
  return new Blob([dailyDefaultEditionItemBytes(audioContent)], { type: "audio/mpeg" });
}

/**
 * Assemble the already-attested edition into the same byte-concatenated MP3
 * shape used by the server's regular dashboard briefing. The browser can then
 * retain one media source, clock, seek range, and audio-focus session for the
 * complete public edition.
 */
export function dailyDefaultEditionPlaylistBlob(items) {
  if (!Array.isArray(items) || items.length < 1 || items.length > 24) {
    throw new TypeError("daily_default_audio_playlist_invalid");
  }
  const encodedItems = [];
  let totalBytes = 0;
  for (const rawItem of items) {
    const item = record(rawItem);
    const validated = validatedDailyDefaultEditionAudio(item?.audioContent);
    totalBytes += validated.decodedLength;
    if (totalBytes > MAX_DAILY_DEFAULT_AUDIO_PLAYLIST_BYTES) {
      throw new TypeError("daily_default_audio_playlist_invalid");
    }
    encodedItems.push(validated);
  }
  return new Blob(encodedItems.map(decodeDailyDefaultEditionAudio), { type: "audio/mpeg" });
}

export const DAILY_DEFAULT_AUDIO_DEVICE_SPEECH_REQUIRED = DEVICE_SPEECH_CODE;
